import { useState } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import H from '../texts/h/H';
import P from '../texts/p/P';
import DropDown, { ItemProp } from '../dropdown/DropDown';
import { executeAnnex } from '../../services/AnnexesService';

interface DownloadAnnexModalProps {
  isOpen: boolean;
  onClose: () => void;
  annexId: number;
  annexName: string;
  requiresYear: boolean;
  requiresMonth: boolean;
}

const DownloadAnnexModal = ({
  isOpen,
  onClose,
  annexId,
  annexName,
  requiresYear,
  requiresMonth,
}: DownloadAnnexModalProps) => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const yearItems: ItemProp[] = Array.from({ length: 10 }, (_, i) => ({
    label: (currentYear - i).toString(),
  }));

  const monthItems: ItemProp[] = [
    { label: 'Enero (1)' },
    { label: 'Febrero (2)' },
    { label: 'Marzo (3)' },
    { label: 'Abril (4)' },
    { label: 'Mayo (5)' },
    { label: 'Junio (6)' },
    { label: 'Julio (7)' },
    { label: 'Agosto (8)' },
    { label: 'Septiembre (9)' },
    { label: 'Octubre (10)' },
    { label: 'Noviembre (11)' },
    { label: 'Diciembre (12)' },
  ];

  const handleYearChange = (yearLabel: string) => {
    setSelectedYear(parseInt(yearLabel));
    setError(null);
  };

  const handleMonthChange = (monthLabel: string) => {
    const monthMatch = monthLabel.match(/\((\d+)\)/);
    if (monthMatch) {
      setSelectedMonth(parseInt(monthMatch[1]));
      setError(null);
    }
  };

  const handleDownload = async () => {
    if (requiresYear && !selectedYear) {
      setError('Debe seleccionar un año');
      return;
    }

    if (requiresMonth && !selectedMonth) {
      setError('Debe seleccionar un mes');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      await executeAnnex(
        annexId,
        requiresYear ? selectedYear || undefined : undefined,
        requiresMonth ? selectedMonth || undefined : undefined,
        annexName
      );

      onClose();
      resetForm();
    } catch (err) {
      setError('Error al descargar el anexo. Por favor, intente nuevamente.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  const resetForm = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setError(null);
    setDownloading(false);
  };

  const needsPeriod = requiresYear || requiresMonth;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Descargar anexo"
      primaryText={downloading ? 'Descargando…' : 'Descargar'}
      primaryVariant="primary"
      onPrimaryAction={handleDownload}
      primaryActionDisabled={downloading}
      loadingPrimaryBtn={downloading}
      secondaryText="Cancelar"
      secondaryVariant="contained"
      onSecondaryAction={handleCancel}
      width="520px"
      overflow="visible"
    >
      <Card margin={{ top: '3', bottom: '3' }} display='flex' flexDirection='column' gap='16px'>
        <Card>
          <P type='2' variant='neutral' withMargin={false}>
            Anexo seleccionado
          </P>
          <H type='4' weight='bold' withMargin={false}>
            {annexName}
          </H>
        </Card>

        {needsPeriod && (
          <P type='2' variant='neutral' withMargin={false}>
            Indicá el período a consultar antes de descargar el archivo.
          </P>
        )}

        {!needsPeriod && (
          <P type='2' variant='neutral' withMargin={false}>
            Este anexo no requiere período. Podés descargarlo directamente.
          </P>
        )}

        {requiresYear && (
          <DropDown
            id="year-selector"
            label="Año de consulta"
            title={selectedYear ? selectedYear.toString() : 'Seleccionar año'}
            items={yearItems}
            onChange={handleYearChange}
            size="md"
            status={error && requiresYear && !selectedYear ? 'error' : 'normal'}
          />
        )}

        {requiresMonth && (
          <DropDown
            id="month-selector"
            label="Mes de consulta"
            title={
              selectedMonth
                ? monthItems.find((item) => item.label.includes(`(${selectedMonth})`))?.label ||
                  'Seleccionar mes'
                : 'Seleccionar mes'
            }
            items={monthItems}
            onChange={handleMonthChange}
            size="md"
            status={error && requiresMonth && !selectedMonth ? 'error' : 'normal'}
          />
        )}

        {error && (
          <Card spacing="3" variant="danger" modifier="weak">
            {error}
          </Card>
        )}
      </Card>
    </Modal>
  );
};

export default DownloadAnnexModal;
